import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatTableModule } from '@angular/material/table'
import { MatSnackBar } from '@angular/material/snack-bar'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { catchError, of, tap } from 'rxjs'

import { LoadingComponent } from '../../../../../components/loading/loading.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../../../components/message-dialog/message-dialog.service'
import {
  BlueBeanOperatorMappingService,
  BlueBeanOperatorMappingView,
  ImportBlueBeanOperatorMappingResultView,
} from '../../../../../services/external-integration/bluebean-integration/bluebean-operator-mapping.service'
import { BlueBeanOperatorMappingSkipReason } from '../../../../../../_generated/graphql'
import { captureException } from '../../../../../util/captureException'
import {
  CustomError,
  CustomErrorCode,
} from '../../../../../util/throwIfCustomError'

@UntilDestroy()
@Component({
  selector: 'app-bluebean-operator-mapping',
  standalone: true,
  imports: [MatTableModule, LoadingComponent],
  templateUrl: './bluebean-operator-mapping.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bluebean-operator-mapping.component.scss',
})
export class BlueBeanOperatorMappingComponent implements OnInit {
  mappings: BlueBeanOperatorMappingView[] = []
  importResult: ImportBlueBeanOperatorMappingResultView | null = null
  isLoading = false
  isImporting = false
  readonly skipReason = BlueBeanOperatorMappingSkipReason
  readonly mappingColumns = ['operatorId', 'name', 'email']
  readonly errorColumns = ['rowNumber', 'operatorId', 'email', 'reason']

  constructor(
    private bluebeanOperatorMappingService: BlueBeanOperatorMappingService,
    private snackBar: MatSnackBar,
    private messageDialogService: MessageDialogService,
  ) {}

  ngOnInit(): void {
    this.loadMappings()
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]
    if (!file) {
      return
    }

    // 取り込みは既存マッピングの全置換（破壊的操作）なので、送信前に確認を挟む。
    this.messageDialogService
      .showConfirm(
        'アップロードした CSV の内容で既存マッピングを全置換します。よろしいですか？',
      )
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (result !== DialogResult.PrimaryButtonClicked) {
          // キャンセル時は同じファイルを選び直せるよう input をリセットする。
          target.value = ''
          return
        }
        this.readAndSubmit(file, target)
      })
  }

  private readAndSubmit(file: File, target: HTMLInputElement): void {
    this.isImporting = true
    this.importResult = null

    const reader = new FileReader()
    reader.onerror = () => {
      this.notifyFileReadFailure(target)
    }
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        this.notifyFileReadFailure(target)
        return
      }
      // result は `data:*;base64,XXX` の形式。base64 本体だけを抽出する。
      const base64Body = result.split(',', 2)[1]
      if (base64Body == null) {
        this.notifyFileReadFailure(target)
        return
      }
      this.submitImport(base64Body, target)
    }
    reader.readAsDataURL(file)
  }

  private notifyFileReadFailure(target: HTMLInputElement): void {
    this.isImporting = false
    this.snackBar.open('ファイルの読み込みに失敗しました。', '閉じる', {
      duration: 6000,
    })
    target.value = ''
  }

  private submitImport(csvBase64: string, fileInput: HTMLInputElement): void {
    this.bluebeanOperatorMappingService
      .importBlueBeanOperatorMapping(csvBase64)
      .pipe(
        tap((result) => {
          if (!result) {
            // throwIfCustomError 経由でも catchError 経由でもない fallback パス。
            // 念のため snackBar でユーザーに通知する。
            this.snackBar.open('CSV のインポートに失敗しました。', '閉じる', {
              duration: 6000,
            })
            return
          }
          this.importResult = result
          this.snackBar.open('マッピングを保存しました。', '閉じる')
          this.loadMappings()
        }),
        catchError((error: unknown) => {
          captureException(error)
          const message =
            error instanceof CustomError &&
            error.code === CustomErrorCode.INVALID_BLUE_BEAN_CSV
              ? `CSV のインポートに失敗しました。${error.message}`
              : 'CSV のインポートに失敗しました。フォーマットを確認してください。'
          this.snackBar.open(message, '閉じる', { duration: 6000 })
          return of(undefined)
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.isImporting = false
        fileInput.value = ''
      })
  }

  private loadMappings(): void {
    this.isLoading = true
    this.bluebeanOperatorMappingService
      .getBlueBeanOperatorMappings()
      .pipe(
        catchError((error: unknown) => {
          captureException(error)
          this.snackBar.open('マッピング一覧の取得に失敗しました。', '閉じる', {
            duration: 6000,
          })
          return of([] as BlueBeanOperatorMappingView[])
        }),
        untilDestroyed(this),
      )
      .subscribe((mappings) => {
        this.mappings = mappings
        this.isLoading = false
      })
  }
}
